import { z } from 'zod';

import { BooleanParamSchema } from '../common/boolean-param/boolean-param';
import { UuidSchema } from '../common/id/id';
import { makeListQuery } from '../common/list-query/list-query';
// The stable-slug format is one rule, not one per domain (U3): a legal document
// is addressed by key exactly as a question is — by clients deep-linking
// `/legal/terms`, by the seeder's create-if-absent check, and by consent records
// that must keep resolving after every rename of the *content*.
import { StableKeySchema } from '../questions/stable-key/stable-key';

import { LegalDocTypeSchema } from './doc-type/doc-type';

/**
 * A legal document — the **container**, not the text (N16).
 *
 * The text lives in `legal_document_versions`, because "the Terms of Service"
 * is a thing that persists across every rewrite of its wording while the
 * wording itself must be frozen the moment somebody accepts it. A document row
 * therefore carries only identity and policy: what it is, whether it is live,
 * and whether members must consent to it.
 *
 * It carries no translatable name. N5 names `legal_document_versions` (content)
 * as the translatable table and nothing else here, and a document's display
 * name is its current version's `title` — which is the string members actually
 * read. Admin surfaces fall back to the key while no version exists yet.
 */
export const LegalDocumentSchema = z
  .object({
    id: UuidSchema,
    key: StableKeySchema,
    docType: LegalDocTypeSchema,
    /**
     * Whether members must accept this document before using gated features.
     *
     * `false` is an informational document (community guidelines, a help
     * article): published, versioned, readable — and never a reason to block
     * anybody. Only `requiresConsent` documents reach the consent gate.
     */
    requiresConsent: z.boolean(),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0),
    /** The current published version, or `null` while the document is only drafts. */
    currentVersion: z.number().int().min(1).nullable(),
    currentVersionId: UuidSchema.nullable(),
    /** The current published version's title in the active locale; `null` when unpublished. */
    title: z.string().nullable(),
  })
  .strict();

export type LegalDocument = z.infer<typeof LegalDocumentSchema>;

/** A document plus its version history — the admin detail view. */
export const LegalDocumentDetailSchema = LegalDocumentSchema.extend({
  versionCount: z.number().int().min(0),
  /** Whether an editable draft already exists (at most one per document). */
  hasDraft: z.boolean(),
}).strict();

export type LegalDocumentDetail = z.infer<typeof LegalDocumentDetailSchema>;

export const CreateLegalDocumentSchema = z
  .object({
    key: StableKeySchema,
    docType: LegalDocTypeSchema,
    requiresConsent: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict();

export type CreateLegalDocumentRequest = z.infer<typeof CreateLegalDocumentSchema>;

/**
 * Updating a document.
 *
 * `key` and `docType` are **absent**, not merely refused. The key is the
 * address every consent record, deep link and seeded row resolves through, and
 * the type decides which client screen renders it — changing either would make
 * an existing document silently become a different one. A schema that cannot
 * express the change is a stronger guarantee than a service that checks for it
 * (the P6 discipline).
 */
export const UpdateLegalDocumentSchema = z
  .object({
    requiresConsent: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .strict();

export type UpdateLegalDocumentRequest = z.infer<typeof UpdateLegalDocumentSchema>;

/** The whitelisted filters of the admin document list (N7). */
export const LegalDocumentsListQuerySchema = makeListQuery({
  docType: LegalDocTypeSchema.optional(),
  requiresConsent: BooleanParamSchema.optional(),
  isActive: BooleanParamSchema.optional(),
  search: z.string().min(1).max(200).optional(),
});

export type LegalDocumentsListQuery = z.infer<typeof LegalDocumentsListQuerySchema>;
